import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseInputDropdownOnBlur,
    setCaptchaEuScript,
} from '../utils';
import { getFormRenderToken } from '../services/anti-bot';
import { isCaptchaEnabled } from '../services/captcha';

Vue.component('rent-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        stores: {
            value: Object,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: '',
            selected: '',
            selectedBrandStore: '',

            dropdownCountry: null,
            dropdownSubject: null,
            dropdownStore: null,

            dropdownHeardFrom: null,
            heardFrom: null,
        };
    },

    computed: {
        moreThenTwoStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 2;
        },

        hasBrandStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 0;
        },
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },
        toggleLoadingButtonClass() {
            return this.submitting ? 'button--loading' : '';
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }
        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });
        handleCloseInputDropdownOnBlur(
            '.new-input__dropdown-container',
            '#store',
        );
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        showDropdown(key) {
            if (!this.hasBrandStores) {
                return false;
            }

            if (!this.countryHandle) {
                return false;
            }

            if (this.countryHandle == key) {
                return true;
            }
        },

        showRadioButtons(key) {
            if (!this.hasBrandStores) {
                return false;
            }

            if (!this.countryHandle) {
                return false;
            }

            if (this.moreThenTwoStores) {
                return false;
            }

            if (this.countryHandle == key) {
                return true;
            }
        },
        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            const data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Promo',
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form, dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('formRenderToken', this.formRenderToken);
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                const response = await axios.post('/', formData);
                const result = response.data;

                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                this.sendGTMData(dataLayerData);

                // Success
                this.formCompleted = true;
                this.errors.clear();

                window.location = result.data.redirectUrl;
                this.scrollToTop();
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(dataToPush) {
            if (!dataToPush) return;
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.dataLayer.push(dataToPush);
            }
        },

        toggleDropdown(event) {
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        setDropdownCountry(event) {
            this.dropdownCountry = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.countryHandle = value;
        },

        setDropdownSubject(event) {
            this.dropdownSubject = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selected = value;
        },

        setDropdownStore(event) {
            this.dropdownStore = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selectedBrandStore = value;
        },

        /**
         * Open or close a custom dropdown element.
         *
         * @param {ClickEvent|ChangeEvent} event
         */
        toggleDropdown(event) {
            let element = event.currentTarget;
            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        /**
         * Update heardFrom value when a selection takes place in its custom dropdown element.
         *
         * @param {ChangeEvent} event
         */
        setDropdownHeardFrom(event) {
            // Set text property to show value in template, set value in heardFrom property to send along with the form.
            this.dropdownHeardFrom = event.currentTarget.dataset.text;
            this.heardFrom = event.currentTarget.dataset.value;

            // Close the dropdown.
            this.toggleDropdown(event);
        },
    },
});
